import React from 'react'
import { Button } from '../../ui/button'

type Props = {
  findUnclaimedBagsStatus: RequestStatusType
  goBack: () => void
  goNext: () => void
  goToFirst?: () => void
  onPageSizeChange?: (pageSize: number) => void
  pageSize?: number
  totalItems: number
  currentPage: number
}

export const PaginationControls = ({
  findUnclaimedBagsStatus,
  goBack,
  goNext,
  goToFirst,
  currentPage,
  onPageSizeChange,
  pageSize = 100,
  totalItems
}: Props) => {
  const isLoading = findUnclaimedBagsStatus === 'pending'
  const canGoBack = currentPage != 1 && !isLoading
  const canGoNext = !isLoading && currentPage * pageSize < totalItems
  const offset = (currentPage - 1) * pageSize
  const totalPages = Math.ceil(totalItems / pageSize)
  const offsetEnd = Math.min(totalItems, pageSize + offset)

  return (
    <div className="flex items-center justify-between px-[var(--spacing-lg)] py-2">
      <div className="text-sm text-gray-600 ">
        {!isLoading && totalItems > 0 && (
          <>
            Showing {offset + 1}
            {offsetEnd != offset + 1 ? <span>- {offsetEnd}</span> : ''}
            {totalItems && ` of ${totalItems}`}
          </>
        )}
      </div>

      <div className="flex items-center space-x-6 lg:space-x-8">
        {onPageSizeChange && (
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium whitespace-nowrap"></p>
            <div className="flex items-center gap-1">
              <label htmlFor="rowsPerPage" className="block mb-1 whitespace-nowrap">
                Rows per page
              </label>
              <select
                id="rowsPerPage"
                value={`${pageSize}`}
                onChange={({ target }) => onPageSizeChange(Number(target.value))}
                className="border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                {[2, 20, 50, 100].map(pageSize => (
                  <option key={`btn-${pageSize}`} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center text-sm font-medium min-w-[120px]">
          {isLoading ? (
            <div className="loadingSpinner" />
          ) : (
            <>
              Page {currentPage} of {totalPages}
            </>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button onClick={goToFirst} disabled={!canGoBack} aria-label="Go to first page">
            <div className="relative flex flex-row ml-[0.5rem]">
              <span className="icon-[fluent--arrow-left-24-regular] absolute right-[0.5rem] size-4" />
              <span className="icon-[fluent--arrow-left-24-regular] size-4" />
            </div>
          </Button>
          <Button onClick={goBack} disabled={!canGoBack} aria-label="Go to previous page">
            <span className="icon-[fluent--arrow-left-24-regular] size-4" />
          </Button>
          <Button onClick={goNext} disabled={!canGoNext} aria-label="Go to next page">
            <span className="icon-[fluent--arrow-right-24-regular] size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
